"use client";

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import {
  CALC_BASE_WIDTH,
  CALC_SCALE,
  HANDLE_BASE_HEIGHT,
  useCalculator,
} from "./CalculatorContext";
import { useFormelbladKurs } from "@/lib/useFormelbladKurs";
import { MATE_KURS_TO_MA, type MaId } from "@/lib/formelblad-shared";
import { useToolLock } from "@/lib/ToolLockContext";
import Calculator from "@/components/mobile/Calculator";

export default function CalculatorOverlay() {
  const { open, pos, closeCalc, setPos, setKeyboardActive, view } =
    useCalculator();
  const kursKod = useFormelbladKurs(open);
  const ma: MaId | null = kursKod
    ? (MATE_KURS_TO_MA[kursKod] ?? null)
    : null;

  const [dragging, setDragging] = useState(false);
  const [hovering, setHovering] = useState(false);
  const { calcLocked: locked } = useToolLock();
  // Låset gäller bara räknar-läget. Multiplikationstabellen är alltid
  // tillgänglig, så i tabell-läget behandlar overlayn fönstret som olåst
  // (ingen grå tint, full interaktion) även om uppgiften låser räknaren.
  const effectiveLocked = locked && view === "calc";
  // Tangentbord aktivt när muspekaren är över räknaren. Drag räknas också
  // som aktivt eftersom pointer-cap kan drifta utanför ramen vid snabba drag.
  // När verktyget är låst (uppgift utan digitala verktyg) blockeras alltid.
  const keyboardActive = !locked && (hovering || dragging);

  // Spegla in i context så UppgiftPage kan trycka tillbaka sin egen
  // ←/→-navigation medan räknaren har fokus.
  useEffect(() => {
    setKeyboardActive(keyboardActive);
  }, [keyboardActive, setKeyboardActive]);
  const offsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const posRef = useRef(pos);

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  function onHandlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest("[data-calc-close]")) return;
    e.preventDefault();
    offsetRef.current = {
      x: e.clientX - posRef.current.x,
      y: e.clientY - posRef.current.y,
    };
    setDragging(true);
  }

  useEffect(() => {
    if (!dragging) return;
    function onMove(e: PointerEvent) {
      setPos({
        x: e.clientX - offsetRef.current.x,
        y: e.clientY - offsetRef.current.y,
      });
    }
    function onEnd() {
      setDragging(false);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
    };
  }, [dragging, setPos]);

  // Always rendered — `display: none` when closed so Calculator's internal
  // state (uttryck, mode, cursor) survives close/open. The component is
  // client-side only, so the hidden cost is a few hundred bytes of state.
  return (
    <div
      role="dialog"
      aria-label="Miniräknare"
      aria-hidden={!open}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        position: "fixed",
        top: pos.y,
        left: pos.x,
        // Render at base width and scale visually — clamping uses CALC_WIDTH
        // (the post-scale visual width) so the overlay stays within viewport.
        width: CALC_BASE_WIDTH,
        maxHeight: `calc((100dvh - 96px) / ${CALC_SCALE})`,
        transform: `scale(${CALC_SCALE})`,
        transformOrigin: "top left",
        // Locked (uppgift utan digitala verktyg): neutral grå overlay,
        // ingen färg-laddning — figur-dimma är medvetet tema-neutral så
        // "låst"-signalen ser likadan ut i alla teman. Idle (ej fokuserad)
        // använder däremot tema-anpassad overlay-idle-tint så miniräknaren
        // smälter in i aktivt tema. Fokuserad = vit (color-bg).
        background: effectiveLocked
          ? "color-mix(in oklch, var(--color-figur-dimma) 55%, var(--color-bg))"
          : keyboardActive
          ? "var(--color-bg)"
          : "color-mix(in oklch, var(--color-overlay-idle-tint) 30%, var(--color-bg))",
        border: effectiveLocked
          ? "1px solid var(--color-figur-dimma)"
          : keyboardActive
          ? "1px solid var(--color-border)"
          : "1px solid var(--color-overlay-idle-tint)",
        borderRadius: "0.5rem",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        zIndex: 40,
        display: open ? "flex" : "none",
        flexDirection: "column",
        userSelect: dragging ? "none" : undefined,
        transition: "background-color 0.18s, border-color 0.18s",
      }}
    >
      <div
        onPointerDown={onHandlePointerDown}
        style={{
          height: HANDLE_BASE_HEIGHT,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 0.75rem",
          background: "var(--color-nav-bg)",
          borderBottom: "1px solid var(--color-border)",
          borderTopLeftRadius: "0.5rem",
          borderTopRightRadius: "0.5rem",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
        }}
      >
        <span aria-hidden="true" />{/* placeholder så stäng-knappen flexar åt höger */}
        <button
          type="button"
          data-calc-close
          onClick={closeCalc}
          aria-label="Stäng miniräknare"
          title="Stäng miniräknaren"
          style={{
            padding: "0.25rem",
            color: "var(--color-nav-text)",
            cursor: "pointer",
            background: "transparent",
            border: "none",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
          >
            <line x1="3" y1="3" x2="11" y2="11" />
            <line x1="3" y1="11" x2="11" y2="3" />
          </svg>
        </button>
      </div>

      <div
        style={{
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
          // När låst (räknar-läget): knappar/display ska inte gå att klicka.
          // Drag-handle ovanför wrappern påverkas inte. I tabell-läget är
          // effectiveLocked false så tabellen + flikarna är fullt klickbara.
          pointerEvents: effectiveLocked ? "none" : undefined,
          opacity: effectiveLocked ? 0.85 : 1,
        }}
      >
        <Calculator ma={ma} keyboardActive={keyboardActive} />
      </div>
    </div>
  );
}
